import React, { useState, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import LockIcon from '@material-ui/icons/Lock'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import CardElement from 'components/provider/CardElement'
import Loader from 'components/general/Loader'
import config from 'config/api/Config'
import { onError } from 'config/lib/errorLib'

import eeLogo from 'resources/images/ee-logo.png'

const stripePromise = loadStripe(config.stripeKey)

// Support / legal details shown in the footer.
const SUPPORT_PHONE = '083 386 5751'
const SUPPORT_EMAIL = 'daniel@energyefficiency.ie'
const CRO_NUMBER = '621300'

// Public, unauthenticated call to the guest add-card endpoints.
async function postCardLink(action, body) {
    const response = await fetch(config.apiGatewayGuest.URL + 'cardlink/' + action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    })
    return response.json()
}

const REASON_MESSAGES = {
    invalid: 'This link is not valid. Please contact us for a new link.',
    expired: 'This link has expired. Please contact us for a new one.',
    used: 'This link has already been used. If you still need to add a card, please contact us.',
    error: 'Something went wrong. Please try again shortly or contact us.'
}

// Format a 'YYYY-MM-DD' date string in long form, e.g. "Wednesday 9 July 2026".
// When todayAware is true and the date is today, returns "Today, 9 July 2026".
function formatLongDate(ymd, todayAware) {
    if (!ymd) return ''
    const d = new Date(ymd + 'T00:00:00')
    if (isNaN(d.getTime())) return ymd
    const day = d.getDate()
    const month = d.toLocaleDateString('en-GB', { month: 'long' })
    const year = d.getFullYear()
    const weekday = d.toLocaleDateString('en-GB', { weekday: 'long' })
    if (todayAware) {
        const t = new Date()
        t.setHours(0, 0, 0, 0)
        if (d.getTime() === t.getTime()) {
            return `Today, ${day} ${month} ${year}`
        }
    }
    return `${weekday} ${day} ${month} ${year}`
}

function euro(n) {
    const v = Number(n)
    if (isNaN(v)) return ''
    return '€' + v.toLocaleString('en-IE', { maximumFractionDigits: 2 })
}

function AddCard(props) {
    const token = props.match.params.token

    const [loading, setLoading] = useState(true)
    const [reason, setReason] = useState(null)          // set when link isn't usable
    const [alreadySetup, setAlreadySetup] = useState(false)
    const [details, setDetails] = useState(null)        // { companyName, leadStartDate, ... }
    const [clientSecret, setClientSecret] = useState(null)
    const [saving, setSaving] = useState(false)
    const [done, setDone] = useState(false)

    useEffect(() => {
        loadLink()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function loadLink() {
        try {
            const result = await postCardLink('get', { token })
            if (!result || !result.valid) {
                setReason((result && result.reason) || 'invalid')
            } else if (result.alreadySetup) {
                setAlreadySetup(true)
                setDetails({ companyName: result.companyName })
            } else {
                setDetails({
                    companyName: result.companyName,
                    leadStartDate: result.leadStartDate,
                    firstBillingDate: result.firstBillingDate,
                    maxCap: result.maxCap,
                    pricePerLead: result.pricePerLead
                })
                setClientSecret(result.clientSecret)
            }
        } catch (e) {
            setReason('error')
            onError(e)
        }
        setLoading(false)
    }

    async function onCardSaved(paymentMethodId) {
        setSaving(true)
        try {
            const result = await postCardLink('complete', { token, paymentMethodId })
            if (result && result.success) {
                setDone(true)
            } else if (result && result.alreadySetup) {
                setAlreadySetup(true)
            } else {
                setReason((result && result.reason) || 'error')
            }
        } catch (e) {
            setReason('error')
            onError(e)
        }
        setSaving(false)
    }

    function returnToLogin() {
        if (props.history) {
            props.history.push('/')
        } else {
            window.location.assign('/')
        }
    }

    function messageWithLogin(text) {
        return (
            <Box width='100%' style={{ textAlign: 'center' }}>
                <Typography variant='body1' style={{ color: '#525f7f' }}>
                    {text}
                </Typography>
                <Box mt={3}>
                    <Button variant='contained' color='primary' onClick={returnToLogin}>
                        Return to login
                    </Button>
                </Box>
            </Box>
        )
    }

    function renderForm() {
        const weeklyLeadCap = details.maxCap
        const pricePerLead = details.pricePerLead
        const hasPrice = pricePerLead !== null && pricePerLead !== undefined && pricePerLead !== ''
        const weeklyMax = hasPrice ? (Number(pricePerLead) * (Number(weeklyLeadCap) || 0)) : null

        const summary = [
            ['Price per lead', hasPrice ? euro(pricePerLead) : '—'],
            ['Weekly lead cap', hasPrice ? `${weeklyLeadCap} leads · max ${euro(weeklyMax)}/week` : `${weeklyLeadCap} leads`],
            ['Leads start', formatLongDate(details.leadStartDate, true)],
            ['First billing', formatLongDate(details.firstBillingDate, false)]
        ]

        return (
            <Box>
                {/* Reassurance sentence */}
                <Box mb={3}>
                    <Typography variant='body2' style={{ color: '#525f7f' }}>
                        Each Monday you're billed only for the previous week's delivered leads. Nothing is
                        charged today. You can pause or cancel any time.
                    </Typography>
                </Box>

                {/* Summary box */}
                <Box
                    mb={3}
                    style={{ border: '1px solid #cfe9d2', background: '#f3faf4', borderRadius: 8, padding: '1rem 1.25rem' }}
                >
                    {summary.map(([label, value], i) => (
                        <Box
                            key={i}
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            style={{ padding: '0.5rem 0', borderBottom: i < summary.length - 1 ? '1px solid #e2f0e4' : 'none' }}
                        >
                            <Typography variant='body2' style={{ color: '#6b7c93' }}>{label}</Typography>
                            <Typography variant='body2' style={{ color: '#1f3a29', fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Card section header */}
                <Box display='flex' justifyContent='space-between' alignItems='center' style={{ marginBottom: '0.5rem' }}>
                    <Box display='flex' alignItems='center'>
                        <LockIcon style={{ color: '#32325d', fontSize: 18, marginRight: 6 }} />
                        <Typography variant='body2' style={{ color: '#32325d', fontWeight: 600 }}>Card details</Typography>
                    </Box>
                    <Typography variant='caption' style={{ color: '#8898aa', textAlign: 'right' }}>
                        Secured by Stripe. We never see or store your card.
                    </Typography>
                </Box>

                {/* Stripe Elements form (unchanged integration) */}
                <Box width='100%'>
                    {saving ? (
                        <Box display='flex' justifyContent='center' alignItems='center' width='100%' style={{ height: '10vh' }}>
                            <Loader />
                        </Box>
                    ) : (
                        <Elements stripe={stripePromise} options={{ clientSecret }}>
                            <CardElement
                                clientSecret={clientSecret}
                                setPaymentMethod={onCardSaved}
                                submitLabel='Activate lead delivery'
                                submitFullWidth
                            />
                        </Elements>
                    )}
                </Box>

                {/* Support line */}
                <Box mt={2.5}>
                    <Typography variant='body2' style={{ color: '#525f7f' }}>
                        Have any questions? Call Daniel on{' '}
                        <a href={'tel:' + SUPPORT_PHONE.replace(/\s/g, '')} style={{ color: 'inherit', textDecoration: 'underline' }}>
                            {SUPPORT_PHONE}
                        </a>{' '}
                        or email{' '}
                        <a href={'mailto:' + SUPPORT_EMAIL} style={{ color: 'inherit', textDecoration: 'underline' }}>
                            {SUPPORT_EMAIL}
                        </a>
                    </Typography>
                </Box>
            </Box>
        )
    }

    function renderBody() {
        if (loading) {
            return (
                <Box display='flex' justifyContent='center' alignItems='center' width='100%' style={{ height: '20vh' }}>
                    <Loader />
                </Box>
            )
        }

        if (reason) {
            return messageWithLogin(REASON_MESSAGES[reason] || REASON_MESSAGES.error)
        }

        if (done) {
            return messageWithLogin(
                "Your card has been saved securely and our team has been notified. You will start receiving your leads shortly!"
            )
        }

        if (alreadySetup) {
            return messageWithLogin(
                "A payment card is already on file for " + ((details && details.companyName) || 'your company') + ". "
                + "There's nothing more to do here. To manage your billing, please log in to your account."
            )
        }

        return renderForm()
    }

    const isFormState = !loading && !reason && !done && !alreadySetup && !!clientSecret

    // Phase 1: while we fetch the link details / client secret, show a single
    // centered loader (no card yet) so the page doesn't render in stages.
    if (loading) {
        return (
            <div style={{ minHeight: '100vh', background: '#f7fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Loader />
            </div>
        )
    }

    let headerTitle = 'Activate lead delivery'
    if (done) {
        headerTitle = 'Thank you!'
    } else if (alreadySetup) {
        headerTitle = "You're all set"
    } else if (!reason && details && details.companyName) {
        headerTitle = `Activate lead delivery for ${details.companyName}`
    }

    return (
        <div style={{ minHeight: '100vh', background: '#f7fafc', boxSizing: 'border-box' }}>
            {/* Logo top-left — matches the login page */}
            <div style={{ padding: '1.5rem 3rem 0' }}>
                <img className='login-logo' src={eeLogo} alt='logo' style={{ maxWidth: '100%' }} />
            </div>

            {/* Large centered header — same feel as the login page header */}
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem 0.5rem' }}>
                <Typography variant='h1' style={{ color: '#32325d', fontWeight: 600 }}>
                    {headerTitle}
                </Typography>
            </div>

            <div style={{ maxWidth: 560, width: '100%', margin: '0 auto', padding: '1.5rem 1rem 3rem', boxSizing: 'border-box' }}>
                {/* Plain block card — no MUI Card (which defaults to overflow:hidden),
                    no flex parent, no fixed height. Lets the Stripe iframe self-size. */}
                <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)', boxSizing: 'border-box' }}>
                    {renderBody()}
                </div>

                {isFormState && (
                    <Typography
                        variant='caption'
                        component='p'
                        style={{ color: '#a0aec0', textAlign: 'center', marginTop: '1.25rem' }}
                    >
                        <a href='https://energyefficiency.ie' target='_blank' rel='noopener noreferrer' style={{ color: 'inherit', textDecoration: 'underline' }}>
                            EnergyEfficiency.ie
                        </a>{' '}
                        is operated by Cyan Bear Limited t/a BeQuick Media, registered in Ireland (CRO no. {CRO_NUMBER})
                    </Typography>
                )}
            </div>
        </div>
    )
}

export default AddCard
